import {
  ChangeEvent, KeyboardEvent, RefObject, useState, useEffect, useCallback, useRef,
} from 'react';

// Interface
// ***********************************
interface ChatTextareaProps {
  id?: string;
  name?: string;
  input: string;
  required?: boolean;
  handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  textareaSizeStyle?: string;
  textareaWrapperSizeStyle?: string;
  textareaAlignmentStyle?: string;
  textareaWrapperAlignmentStyle?: string;
  customWrapperStyle?: string;
  customTextareaStyle?: string;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  /** Max visible rows when collapsed (default 4). Expand button appears when content exceeds this. */
  collapsedLineLimit?: number;
  /** Max visible rows when expanded (default 15). */
  expandedLineLimit?: number;
  /** Called when expand state changes so parent can render the expand/collapse button externally. */
  onExpandStateChange?: (state: { showToggle: boolean; isExpanded: boolean; toggle: () => void }) => void;
}

/**
 * @description Chat textarea component with auto-resize using dynamic rows.
 * Much simpler approach: just count lines and set the rows attribute accordingly.
 * Capped at collapsedLineLimit when collapsed, expandedLineLimit when expanded.
 */
export const ChatTextarea = (props: ChatTextareaProps) => {
  const {
    id = 'chat-textarea',
    name = 'chat-textarea',
    input,
    required = true,
    handleInputChange,
    handleKeyDown,
    placeholder,
    textareaSizeStyle = 'w-full',
    textareaWrapperSizeStyle = 'w-full',
    textareaAlignmentStyle = 'mx-0 p-0',
    textareaWrapperAlignmentStyle = 'm-2',
    customWrapperStyle = 'transition-none',
    customTextareaStyle = 'appearance-none border-0 shadow-none outline-none transition-none focus:ring-0 active:border-0 active:ring-0',
    textareaRef,
    collapsedLineLimit = 4,
    expandedLineLimit = 15,
    onExpandStateChange,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [currentRows, setCurrentRows] = useState(1);
  const internalRef = useRef<HTMLTextAreaElement>(null);

  // Use the external ref if provided, otherwise use internal
  const resolvedRef = textareaRef || internalRef;

  const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  // Notify parent of expand state changes
  useEffect(() => {
    onExpandStateChange?.({ showToggle, isExpanded, toggle });
  }, [showToggle, isExpanded, toggle, onExpandStateChange]);

  // When content shrinks below the limit, auto-collapse
  useEffect(() => {
    if (!showToggle && isExpanded) {
      setIsExpanded(false);
    }
  }, [showToggle, isExpanded]);

  /**
   * Calculate how many lines the content actually needs.
   * Uses scrollHeight vs lineHeight to get accurate line count.
   */
  const calculateNeededLines = useCallback((): number => {
    const textarea = resolvedRef.current;
    if (!textarea || !input) return 1;

    // Save originals
    const originalRows = textarea.rows;
    const originalMinHeight = textarea.style.minHeight;
    const originalOverflow = textarea.style.overflow;
    const originalTransition = textarea.style.transition;

    // Neutralize constraints for accurate measurement
    textarea.style.transition = 'none'; // prevent animated resize
    textarea.style.minHeight = '0'; // remove min-height so rows=1 actually shrinks
    textarea.style.overflow = 'hidden'; // prevent scrollbar during measurement
    textarea.rows = 1; // shrink to minimum to get true scrollHeight

    // Force reflow so the browser applies the changes before reading scrollHeight
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    textarea.scrollHeight;

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;

    const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom;
    const neededLines = Math.ceil(contentHeight / lineHeight);

    // Restore originals
    textarea.rows = originalRows;
    textarea.style.minHeight = originalMinHeight;
    textarea.style.overflow = originalOverflow;
    textarea.style.transition = originalTransition;

    return Math.max(1, neededLines);
  }, [resolvedRef, input]);

  /**
   * Update rows based on content and expand state.
   * This is the core logic that replaces all the height manipulation.
   */
  const updateRows = useCallback(() => {
    const neededLines = calculateNeededLines();

    // Determine if we should show the expand toggle
    // Show toggle when content needs more lines than collapsed limit
    setShowToggle(neededLines > collapsedLineLimit);

    // Set the actual rows to display
    let displayRows: number;

    if (isExpanded) {
      // When expanded: show all lines up to expanded limit
      displayRows = Math.min(neededLines, expandedLineLimit);
    } else {
      // When collapsed: show lines up to collapsed limit
      displayRows = Math.min(neededLines, collapsedLineLimit);
    }

    setCurrentRows(displayRows);
  }, [calculateNeededLines, collapsedLineLimit, expandedLineLimit, isExpanded]);

  // Initial measurement on mount (synchronous, no RAF delay)
  useEffect(() => {
    updateRows();
  }, [updateRows]);

  // Update rows whenever input or expand state changes
  useEffect(() => {
    // Use RAF to ensure DOM has updated
    const raf = requestAnimationFrame(() => {
      updateRows();
    });
    return () => cancelAnimationFrame(raf);
  }, [input, isExpanded, updateRows]);

  // Scroll to top when expanding
  useEffect(() => {
    if (isExpanded && resolvedRef.current) {
      resolvedRef.current.scrollTop = 0;
    }
  }, [isExpanded, resolvedRef]);

  // Render component
  // ****************************************************
  return (
    <div
      className={`
        min-w-0
        ${textareaWrapperSizeStyle}
        ${textareaWrapperAlignmentStyle}
        ${customWrapperStyle}
      `}
    >
      <textarea
        ref={resolvedRef}
        className={`resize-none transition-none ${textareaSizeStyle} ${textareaAlignmentStyle} ${customTextareaStyle}`}
        id={id}
        name={name}
        rows={currentRows}
        onChange={handleInputChange}
        placeholder={placeholder}
        value={input}
        onKeyDown={handleKeyDown}
        required={required}
        style={{
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          overflow: showToggle ? 'auto' : 'hidden',
        }}
      />
    </div>
  );
};
